import React, {useContext, useState} from "react";
import {Button} from 'primereact/button';
import {apiRequest, formatFileSize, toast, url} from "@/Helpers/general";
import ConfirmDialogTemplate from '@/Components/Templates/ConfirmDialogTemplate';
import {AppContext} from "@/Components/Context/AppContext";
import {createStorageObject} from "@/Helpers/storage";
import FileUploadTemplate from "@/Components/Templates/Forms/FileUploadTemplate";
import {CLAIM_DOCUMENTS_ALLOWED_EXTENSIONS, OCR_DOCUMENTS_ALLOWED_EXTENSIONS} from "@/Config/constants";
import {Img} from "react-image";
import SupportedOcrChos from "@/Components/Partials/SupportedOcrChos";

/**
 * Component which handles the "automated parsing (OCR)" tab of creating a new claim
 */
const NewClaimOCRForm = () => {
    const {toastRef} = useContext(AppContext);

    const [formLoading, setFormLoading] = useState(false);
    const [mainFiles, setMainFiles] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [showMandatoryFileMsg, setShowMandatoryFileMsg] = useState(false);
    const [duplicateDialogConfirmMessage, setDuplicateDialogConfirmMessage] = useState(null);
    const [formErrors, setFormErrors] = useState([]);

    /**
     * Submits the new claim form
     */
    const submitForm = async (force) => {
        if (mainFiles) {
            setShowMandatoryFileMsg(false);
            setFormLoading(true);

            let formData = new FormData();

            if (force) {
                formData.append('force', true);
            }

            mainFiles.forEach((mainFile) => {
                formData.append('mainFiles[]', mainFile);
            });

            if (attachments) {
                attachments.forEach((file) => {
                    formData.append('attachments[]', file);
                });
            }

            apiRequest('post', 'request/ocr', {
                data: formData,
                onSuccess: (response) => {

                    setFormLoading(false);

                    if (response?.data?.responseType == 'success') {
                        createStorageObject('flashMessages', [{
                            type: 'success',
                            title: 'Success',
                            message: 'The claim has been created.'
                        }]);

                        window.location.replace(url('home'));

                    } else {
                        toast(toastRef, 'error', 'Error creating claim.');
                    }
                },
                onFailure: (error) => {
                    setFormLoading(false);
                    if (error.status == 403) {
                        if (error.messages.includes('already uploaded')) {
                            setDuplicateDialogConfirmMessage(error.messages);
                        }
                    } else {
                        setFormErrors(Object.values(error.messages));
                    }
                }
            });

        } else {
            setShowMandatoryFileMsg(true);
        }
    };

    /**
     * Add files in the state
     *
     * @param {object} event data related to the files
     */
    const uploadHandler = (event, type) => {
        switch (type) {
            case 'mainFiles':
                setMainFiles(event.files);
                setShowMandatoryFileMsg(false);
                break;

            case 'attachments':
                setAttachments(event.files);
                break;

            default:
                break;
        }
    };

    /**
     * Generate a preview of a file
     *
     * @param {object} doc data related to the file
     * @param uploadPreview
     */
    const filePreview = (doc, uploadPreview = false) => {
        let preview = "";
        let type = uploadPreview ? doc?.type : doc?.file_mime;

        switch (type) {
            case "application/pdf":
                preview = (
                    <div className="no-picture">
                        <i className="pi pi-file-pdf"></i>
                    </div>
                );
                break;
            case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                preview = (
                    <div className="no-picture">
                        <i className="pi pi-file-word"></i>
                    </div>
                );
                break;
            case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                preview = (
                    <div className="no-picture">
                        <i className="pi pi-file-excel"></i>
                    </div>
                );
                break;
            case "application/msword":
                preview = (
                    <div className="no-picture">
                        <i className="pi pi-file-word"></i>
                    </div>
                );
                break;
            case "application/vnd.ms-excel":
                preview = (
                    <div className="no-picture">
                        <i className="pi pi-file-excel"></i>
                    </div>
                );
                break;
            default:
                preview = (
                    <div className="no-picture">
                        <i className="pi pi-file"></i>
                    </div>
                );
                break;
        }

        if (type.search("image") !== -1) {
            let getImg = uploadPreview ? doc.objectURL : imgUrls.find((img) => img.id == doc.id);

            if (getImg) {
                preview = (
                    <Img
                        src={getImg.url}
                        alt=""
                        unloader={
                            <div className="no-picture">
                                <i className="pi pi-image"></i>
                            </div>
                        }
                    />
                );
            }
        }

        return preview;
    };


    /**
     * Remove a file from the attachments
     *
     * @param {object} event data related to the file
     */
    const onAttachmentRemove = (event) => {
        let copyAttachments = [...attachments];

        if (event?.file?.name) {
            copyAttachments = copyAttachments.filter((file) => file.name != event.file.name);
        }

        setAttachments(copyAttachments);
    };

    /**
     * Remove a file from the attachments
     *
     * @param {object} event data related to the file
     */
    const onPaymentPackRemove = (event) => {
        let copyPaymentPacks = [...mainFiles];

        if (event?.file?.name) {
            copyPaymentPacks = copyPaymentPacks.filter((file) => file.name != event.file.name);
        }

        setMainFiles(copyPaymentPacks);
    };

    /**
     * The preview (for files other than images) which appears after you select a file
     */
    const itemTemplate = (file, event) => {
        return (
            <div className="p-fileupload-row other-files-format">
                <div className="file-extension-icon">{filePreview(file, true)}</div>

                <div className="p-fileupload-filename">{file.name}</div>

                <div>{formatFileSize(file.size)}</div>

                <div>
                    <button type="button" className="p-button p-component p-button-icon-only" onClick={() => {
                        event.onRemove();
                    }}>
                        <span className="p-button-icon p-c pi pi-times"></span>
                        <span className="p-button-label p-c">&nbsp;</span>
                    </button>
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="general-form">
                <div className="main-content grid">
                    <div className="col-6">
                        <div className={'flex gap-1 align-items-center block mb-3'}>
                            <label>Files for parsing:</label>
                            <SupportedOcrChos/>
                        </div>
                        <FileUploadTemplate uploadHandler={(e) => uploadHandler(e, 'mainFiles')}
                                            multiple={true}
                                            onFileRemove={onPaymentPackRemove}
                                            accept={OCR_DOCUMENTS_ALLOWED_EXTENSIONS.join(',')}
                                            itemTemplate={itemTemplate}

                        />
                        {showMandatoryFileMsg &&
                            <small className={'p-error block mt-1 w-full'}>PDF file is mandatory.</small>}
                    </div>


                    <div className="col-6">
                        <label htmlFor="" className="block mb-3">Additional attachment files:</label>
                        <FileUploadTemplate uploadHandler={(e) => uploadHandler(e, 'attachments')}
                                            onFileRemove={onAttachmentRemove}
                                            accept={CLAIM_DOCUMENTS_ALLOWED_EXTENSIONS.join(',')}
                                            itemTemplate={itemTemplate}
                        />
                        {}
                    </div>
                </div>
                {formErrors?.map((error, key) => (
                    <small key={`error-message-${key}`} className={'p-error block mt-1 w-full'}>{error}</small>
                ))}

                <hr/>
                <div className="text-right">
                    <Button label="Save"
                            onClick={() => submitForm(false)} loading={formLoading}
                            disabled={!mainFiles.length}
                            loadingIcon="pi pi-spin pi-sun" iconPos="center"/>
                </div>
            </div>
            <ConfirmDialogTemplate
                title="Duplicate file"
                message={<span dangerouslySetInnerHTML={{__html: duplicateDialogConfirmMessage ?? ''}}/>}
                onConfirm={() => {
                }}
                visible={!!duplicateDialogConfirmMessage}
                setVisible={setDuplicateDialogConfirmMessage}
                footer={
                    <>
                        <Button
                            label="Continue processing"
                            className="p-button-outlined mr-3"
                            onClick={() => {
                                submitForm(true);
                                setDuplicateDialogConfirmMessage(null);
                            }}
                            loading={formLoading}
                            loadingIcon="pi pi-spin pi-sun"
                            iconPos="center"
                        />
                        <Button
                            label="Cancel"
                            className="p-button-outlined p-button-secondary"
                            onClick={() => {
                                setDuplicateDialogConfirmMessage(null);
                            }}
                        />
                    </>
                }
            />

        </>
    );
};

export default NewClaimOCRForm;
